import * as React from "react";
import {useCallback, useMemo, useRef} from "react";
import {useSelector} from "react-redux";
import {Location, matchPath} from "react-router-dom";
import generateRoutes from '../helpers/route';
import {RootState} from "../redux/store";
import NewsArticle from '../views/NewsArticle';
import NewsTopic from '../views/NewsTopic';
import useCurrentRoute from "./useCurrentRoute";

export const INITIAL_ROUTE = {
  articles: [],
  articles_visible: [],
  articles_ready: [],
  articles_loading: [],
  articles_failed: [],
  articles_count: null,
  articles_unavailable: null,
}

export type RouteDef = React.ComponentType<any>;

export type RoutesDef = {
  [key: string]: RouteDef;
}

type useRoutesDef = () => {
  matches: (route: Location) => string;
  routes: React.ReactElement[]
};

const useRoutes: useRoutesDef = () => {
  const pathname = useCurrentRoute();
  const {articles, base, topics} = useSelector((state: RootState) => {
    const articles = Object.values(state.entities.articles.byId);
    const topics = Object.values(state.entities.topics.byId);
    const {config} = state.application;
    return {
      articles,
      base: config.base,
      topics,
    }
  });
  const entries = useRef({articles: 0, topics: 0});
  const route_map = useRef(null);

  const routeElements = useMemo((): React.ReactElement[] => {
    if (!base || !articles || !articles.length || !topics || !topics.length) return;
    if (articles.length === entries.current.articles && topics.length === entries.current.topics) return route_map.current;

    const routes: RoutesDef = {};

    entries.current.articles = articles.length;
    articles.forEach((article) => {
      if (!article) return;

      const key = article.slug;
      routes[`${base}/${key}`] = NewsArticle;
    });

    entries.current.topics = topics.length;
    topics.forEach((topics) => {
      if (!topics) return;

      const key = topics.slug;
      routes[`${base}/${key}`] = NewsTopic;
    });

    // new route map
    route_map.current = generateRoutes(routes);
    return route_map.current;
  }, [base, articles, topics]);

  const matches = useCallback(() => {
    if (!articles) return;

    const match = routeElements.find(({props}: {props: {path: string}}) => matchPath(pathname, props.path));

    if (!match) {
      console.error(`Unknown location ${pathname} checked`);
      return;
    } else {
      return String(match.key).replace(/\/$/, ""); // remove trailing slash
    }
  }, [articles, pathname, routeElements]);

  return {
    matches,
    routes: routeElements || [],
  };
}

export default useRoutes;
